import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  PaperClipIcon,
  LockClosedIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import EvidenceLinkBrowser from '../../components/EvidenceLinkBrowser'
import PatronScreeningToolModal from './components/PatronScreeningToolModal'
import SearchAuditPanel from './components/SearchAuditPanel'
import EcddAmlPepPanel from './components/EcddAmlPepPanel'
import EcddDocumentPreviewModal from './components/EcddDocumentPreviewModal'
import { printEcddPreview } from '../../utils/ecddPreviewPrint'
import EcddAttachedDocumentsRail from './components/EcddAttachedDocumentsRail'
import EcddDetailNav from './components/EcddDetailNav'
import EcddDecisionPanel from './components/EcddDecisionPanel'
import { buildEcddNavSections, firstRequiredEcddSection, scrollToEcddSection } from './components/ecddDetailNavSections'
import PatronIdBadge from '../../components/PatronIdBadge'
import { resolvePatronIdentity } from '../../utils/patronIdentity'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { isPatronScreeningPrototypeEnabled } from '../../config/featureFlags'
import { usePatronScreeningToolEnabled } from '../../hooks/usePatronScreeningToolEnabled'
import { useAuth } from '../../contexts/AuthContext'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'
import { FormsModuleGate, RecordFormsPanel } from '../forms'
import { fetchSubjectFormResponses } from '../forms/formResponseHelpers'
import { formatSydneyDateTime } from '../../utils/sydneyDateTime'
import {
  DEFAULT_ECDD_FORM_ANSWERS,
  mergeFormAnswers,
  evaluateEcddFormGates,
  formAnswersToTriggerCheck,
  EcddTriggerFields,
  EcddKycFields,
  EcddSofFields,
  EcddActivityFields,
  EcddEngageFields,
  EcddScreeningFields,
  isLargePayoutTrigger,
  hasScreeningOnRecord,
  patchFormFromAmlHistory,
} from './components/EcddStructuredForm'

const ECDD_DETAIL_HELP_SCREEN_KEY = 'page:compliance-safe/ecdd/detail'

// Task template config — task status/assignments loaded from API
const INITIAL_BUCKETS = [
  {
    id: 'sof',
    title: 'Source of Funds / Source of Wealth',
    icon: '💰',
    description: "Identify where the customer's money comes from — both for specific transactions and their general wealth.",
    items: [
      { id: 'sof_1', label: 'Request source of funds declaration from patron', guidance: 'Ask the patron to explain the source of funds for recent gaming activity.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null },
      { id: 'sof_2', label: 'Verify source of funds documentation', guidance: 'Review any supporting documents provided.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null },
      { id: 'sof_3', label: 'Assess source of wealth (overall)', guidance: 'For PEPs, assess the customer\'s overall wealth.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null },
    ],
  },
  {
    id: 'verify',
    title: 'Additional Identification / Re-verification',
    icon: '🔍',
    description: 'Collect additional identity information or re-verify existing KYC.',
    items: [
      { id: 'ver_1', label: 'Re-verify existing KYC documents', guidance: 'Confirm the current KYC record is still accurate.', mandatory: false, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null, isKyc: true, kycDocs: [] },
      { id: 'ver_2', label: 'Collect additional identification (occupation, employer)', guidance: 'For PEPs, collect details of their political role.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null, isKyc: true, kycDocs: [] },
    ],
  },
  {
    id: 'txn',
    title: 'Transaction Analysis',
    icon: '📊',
    description: 'Detailed analysis of the customer\'s gaming and transaction history.',
    items: [
      { id: 'txn_1', label: 'Review payout history (last 12 months)', guidance: 'View payout history from the system. Filter, export to XLS and attach if needed.', mandatory: false, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: 'payout_history' },
      { id: 'txn_2', label: 'Review gaming session data', guidance: 'View gaming session data from the system.', mandatory: false, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: 'gaming_session' },
    ],
  },
  {
    id: 'approval',
    title: 'Senior Management Approval',
    icon: '✅',
    description: 'For foreign PEPs: senior management must approve whether to continue providing services.',
    items: [
      { id: 'app_1', label: 'Prepare recommendation for management', guidance: 'Summarise findings from all ECDD measures.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [] },
      { id: 'app_2', label: 'Obtain senior management approval', guidance: 'Submit the completed ECDD package for review.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [] },
    ],
  },
]

const ITEM_STATUS = {
  not_started: { label: 'Not Started', color: '#6B7280', bg: '#f8fafc', ring: '#e5e7eb' },
  in_progress: { label: 'In Progress', color: '#ca8a04', bg: '#fefce8', ring: '#fef08a' },
  done: { label: 'Done', color: '#16a34a', bg: '#f0fdf4', ring: '#bbf7d0' },
  na: { label: 'N/A', color: '#6B7280', bg: '#f3f4f6', ring: '#e5e7eb' },
}

/** Required checklist rows are satisfied by Done or N/A (not applicable). */
function isMandatoryResolved(status) {
  return status === 'done' || status === 'na'
}

/** Normalize ecdd_records.buckets_snapshot (array or { buckets, decision, formAnswers, … }) into UI state. */
function hydrateEcddDraftFromRecord(record) {
  if (!record) return null
  const snap = record.bucketsSnapshot
  let nextBuckets = null
  let decision = record.resolution || null
  let rationale = null
  let formAnswers = null

  if (Array.isArray(snap) && snap.length) {
    nextBuckets = snap
  } else if (snap && typeof snap === 'object') {
    if (Array.isArray(snap.buckets) && snap.buckets.length) nextBuckets = snap.buckets
    decision = snap.decision || decision
    rationale = snap.decisionRationale || null
    if (snap.formAnswers && typeof snap.formAnswers === 'object') formAnswers = snap.formAnswers
  }

  if (!nextBuckets && !formAnswers) return null
  return { buckets: nextBuckets, decision, rationale, formAnswers }
}

function seedFormAnswersFromRecord(record, existing) {
  if (existing && typeof existing === 'object') {
    return mergeFormAnswers(existing)
  }
  const t = String(record?.triggerType || '').toLowerCase()
  const mapped =
    t === 'large_payout' || t.includes('large_payout') || (t.includes('payout') && t.includes('large'))
      ? 'large_payout'
      : t.includes('pep')
        ? 'pep'
        : t.includes('alert') || t.includes('rule')
          ? 'alert'
          : t.includes('smr')
            ? 'postsmr'
            : t.includes('risk')
              ? 'risk'
              : t.includes('unusual') || t.includes('complex')
                ? 'unusual'
                : t.includes('manual') || t.includes('obs')
                  ? 'manual'
                  : ''
  return mergeFormAnswers({
    triggerType: mapped,
    linkedAlertId: record?.linkedAlert || '',
    priorSmr: record?.linkedSmr || '',
    triggerDesc: record?.trigger || '',
    triggerDate: record?.triggerDate || '',
  })
}

/** API notes_log → display rows (supports legacy { date, who, action }). */
function normalizeEcddNotes(raw) {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((n) => n && (n.text != null || n.action != null))
    .map((n, i) => ({
      id: n.id || `note_${i}`,
      ts: n.ts || n.date || '',
      who: n.who || 'User',
      text: n.text != null ? String(n.text) : String(n.action || ''),
    }))
}

function formatEcddNoteTs(ts) {
  if (!ts) return '—'
  try {
    const d = new Date(ts)
    if (Number.isNaN(d.getTime())) return String(ts)
    return formatSydneyDateTime(d)
  } catch {
    return String(ts)
  }
}

function Badge({ bg, color, border, children }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: bg, color, border: border ? `1px solid ${border}` : undefined }}
    >
      {children}
    </span>
  )
}



function ChecklistItem({ item, onStatusChange, onFindingsChange, onAttachDocuments, memberId, memberName, onShowActivityEvidence, caseId, readOnly = false }) {
  const [expanded, setExpanded] = useState(item.status === 'in_progress')
  const [findingsDraft, setFindingsDraft] = useState(item.findings || '')
  const [uploading, setUploading] = useState(false)
  const st = ITEM_STATUS[item.status] || ITEM_STATUS.not_started
  const fileInputRef = useRef(null)
  const kycFileInputRef = useRef(null)

  useEffect(() => {
    setFindingsDraft(item.findings || '')
  }, [item.findings])

  const handleFilesSelected = async (e) => {
    const files = e.target.files
    if (!files?.length || !caseId) return
    
    setUploading(true)
    try {
      const uploadedNames = []
      for (const file of Array.from(files)) {
        const fd = new FormData()
        fd.append('file', file)
        
        const res = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/ecdd/documents`),
          { method: 'POST', body: fd }
        )
        
        if (res.ok) {
          uploadedNames.push(file.name)
        }
      }
      
      if (uploadedNames.length > 0 && onAttachDocuments) {
        onAttachDocuments(item.id, uploadedNames)
      }
    } catch (err) {
      console.error('Upload failed:', err)
      alert('Failed to upload one or more files')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const unresolvedRequired = item.mandatory && !isMandatoryResolved(item.status)
  const isNa = item.status === 'na'
  const isDone = item.status === 'done'

  return (
    <div
      className="rounded-lg mb-2 overflow-hidden border"
      style={{
        borderColor: unresolvedRequired ? '#fecaca' : '#f1f5f9',
        background: isDone ? '#fafffe' : isNa ? '#f8fafc' : '#fff',
      }}
    >
      <div onClick={() => setExpanded(!expanded)} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50/50">
        <div
          className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
          style={{
            border: `2px solid ${st.ring}`,
            background: isDone ? '#16a34a' : isNa ? '#94a3b8' : 'transparent',
            color: '#fff',
          }}
        >
          {isDone ? '✓' : isNa ? '–' : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <span className={`text-sm font-semibold ${isDone ? 'text-green-600' : isNa ? 'text-slate-500' : 'text-psg-teal'}`}>
              {item.label}
            </span>
            {/* "Required" only while still outstanding — never beside Done/N/A status */}
            {unresolvedRequired ? (
              <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">Required</Badge>
            ) : null}
          </div>
          {item.assignedTo && (
            <span className="text-xs text-gray-500">
              Assigned: {item.assignedTo}
              {item.completedDate && ` • Completed: ${item.completedDate}`}
            </span>
          )}
        </div>
        <div
          className="relative flex-shrink-0 flex items-center"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <select
            value={item.status}
            onChange={(e) => onStatusChange(item.id, e.target.value)}
            disabled={readOnly}
            aria-label={`Status: ${item.label}`}
            className="appearance-none text-xs font-semibold rounded-full border pl-2.5 pr-7 py-1 cursor-pointer max-w-[9.5rem] truncate disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ color: st.color, backgroundColor: st.bg, borderColor: st.ring }}
          >
            {['not_started', 'in_progress', 'done', 'na'].map((s) => (
              <option key={s} value={s}>
                {ITEM_STATUS[s].label}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {(item.docs?.length > 0 || item.kycDocs?.length > 0) && <PaperClipIcon className="w-4 h-4 text-gray-400" />}
        <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-0 ml-8 border-t border-gray-50">
          <div className="p-3 bg-gray-50 rounded-lg mt-3 mb-3 border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Guidance</div>
            <div className="text-sm text-gray-500 leading-relaxed">{item.guidance}</div>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-semibold text-gray-600 mb-1.5">Findings / Notes</label>
            <textarea
              value={findingsDraft}
              readOnly={readOnly || !onFindingsChange}
              onChange={(e) => {
                const v = e.target.value
                setFindingsDraft(v)
                if (!readOnly && onFindingsChange) onFindingsChange(item.id, v)
              }}
              onBlur={() => {
                if (!readOnly && onFindingsChange && findingsDraft !== (item.findings || '')) {
                  onFindingsChange(item.id, findingsDraft)
                }
              }}
              placeholder="Record what was found, observed, or confirmed..."
              className="w-full min-h-[140px] p-3 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white resize-y outline-none focus:border-gray-300 read-only:bg-gray-50"
            />
          </div>
          {/* System data: View from platform (digital-first) */}
          {(item.dataSource === 'payout_history' || item.dataSource === 'gaming_session') && (
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Data from system</label>
              <p className="text-xs text-gray-500 mb-2">View data directly from the platform. Filter, export to XLS and attach if needed.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onShowActivityEvidence?.(item.dataSource === 'payout_history' ? 'payouts' : 'combined')}
              >
                <EyeIcon className="w-3 h-3 mr-1" />
                Review & link activity
              </Button>
            </div>
          )}
          {/* KYC: Documents collected + Submit for KYC */}
          {item.isKyc && (
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Documents collected</label>
              {item.kycDocs?.length > 0 ? (
                <div className="flex gap-2 flex-wrap mb-2">
                  {item.kycDocs.map((d, i) => (
                    <span key={i} className="flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-gray-100 text-sm text-gray-600">
                      <PaperClipIcon className="w-3 h-3" />{d}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mb-2">No documents collected yet.</p>
              )}
              <input
                ref={kycFileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={handleFilesSelected}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-psg-teal border-psg-teal hover:bg-psg-teal/5"
                onClick={(e) => {
                  e.stopPropagation()
                  kycFileInputRef.current?.click()
                }}
                disabled={uploading}
              >
                <ArrowUpTrayIcon className="w-3 h-3 mr-1" /> {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
              <Button size="sm" className="ml-2 bg-psg-teal hover:bg-psg-teal-700">
                <ShieldCheckIcon className="w-3 h-3 mr-1" /> Submit for KYC
              </Button>
            </div>
          )}
          {/* Attachments (for SoF and other upload-only items) */}
          {!item.dataSource && !item.isKyc && (
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Attachments</label>
              {item.docs?.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {item.docs.map((d, i) => (
                    <span key={i} className="flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-gray-100 text-sm text-gray-500">
                      <PaperClipIcon className="w-3 h-3" />{d}
                    </span>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={handleFilesSelected}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
                disabled={uploading}
              >
                <ArrowUpTrayIcon className="w-3 h-3 mr-1" /> {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          )}
          {/* Attachments for dataSource items (exported file) */}
          {item.dataSource && item.docs?.length > 0 && (
            <div className="mt-2">
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Attached (exported)</label>
              <div className="flex gap-2 flex-wrap">
                {item.docs.map((d, i) => (
                  <span key={i} className="flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-gray-100 text-sm text-gray-500">
                    <PaperClipIcon className="w-3 h-3" />{d}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SectionAnchor({ id, children }) {
  return (
    <div id={`ecdd-section-${id}`} className="scroll-mt-24">
      {children}
    </div>
  )
}

/** Form-style numbered section wrapper. `documentMode` = always open, no accordion. */
function FormSection({
  number,
  title,
  status,
  statusLabel,
  children,
  defaultOpen = true,
  description,
  documentMode = false,
}) {
  const [collapsed, setCollapsed] = useState(!defaultOpen)
  const isCollapsed = documentMode ? false : collapsed
  const statusStyles = {
    required: { bg: '#fef2f2', color: '#dc2626' },
    optional: { bg: '#f1f5f9', color: '#94a3b8' },
    done: { bg: '#f0fdf4', color: '#15803d' },
    conditional: { bg: '#fffbeb', color: '#b45309' },
  }
  const st = statusStyles[status] || statusStyles.optional

  if (documentMode) {
    return (
      <section className="mb-6 pb-6 border-b border-slate-200 last:border-b-0 last:mb-0 last:pb-0">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-[26px] h-[26px] rounded-full bg-psg-teal text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
            {number}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex gap-2 items-center flex-wrap">
              <h2 className="text-base font-semibold text-[#073B4D] m-0">{title}</h2>
              {statusLabel && (
                <span
                  className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
                  style={{ background: st.bg, color: st.color }}
                >
                  {statusLabel}
                </span>
              )}
            </div>
            {description && <p className="text-xs text-slate-500 mt-1 mb-0">{description}</p>}
          </div>
        </div>
        <div className="pl-0 sm:pl-9">{children}</div>
      </section>
    )
  }

  return (
    <Card className="mb-4 overflow-hidden border border-slate-100">
      <div
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-slate-50/80 transition-colors border-b border-slate-100 bg-white"
      >
        <div className="w-[26px] h-[26px] rounded-full bg-psg-teal text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
          {number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <h2 className="text-[15px] font-semibold text-[#073B4D] m-0">{title}</h2>
            {statusLabel && (
              <span
                className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
                style={{ background: st.bg, color: st.color }}
              >
                {statusLabel}
              </span>
            )}
          </div>
          {description && !isCollapsed && (
            <p className="text-xs text-slate-500 mt-1 mb-0">{description}</p>
          )}
        </div>
        <ChevronRightIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
      </div>
      {!isCollapsed && (
        <CardContent className="px-4 sm:px-5 py-4">
          {children}
        </CardContent>
      )}
    </Card>
  )
}

/**
 * Keep supporting checklist buckets in sync with structured form answers
 * (e.g. SoF "not required" must waive checklist SoF Required items).
 */
function syncBucketsFromFormAnswers(buckets, answers, meta = {}) {
  const foreignPep = answers.scrResult === 'hit' && answers.scrDisp === 'true' && answers.pepClass === 'foreign'
  const sofReq = foreignPep ? 'yes' : answers.sofRequired
  const sofWaived = sofReq === 'no' && String(answers.sofNoReason || '').trim().length > 0
  const sofCompleted =
    sofReq === 'yes' &&
    answers.sofMethod &&
    answers.sofDate &&
    answers.sofBy &&
    answers.sofCat &&
    answers.sofResp &&
    answers.sofAssess
  const kycCurrent = answers.kycCurrent === 'yes'
  const decision = String(meta.decision || '').trim()
  const decisionRationale = String(meta.decisionRationale || '').trim()
  const hasDecision = !!decision && !!decisionRationale

  const waiveItem = (item, reason, code = 'exemption_applies') => ({
    ...item,
    status: 'na',
    skip_reason_code: code,
    skip_reason_notes: reason,
    findings: reason,
    autoLinkedFromForm: true,
  })

  const completeItem = (item, findings) => ({
    ...item,
    status: 'done',
    skip_reason_code: undefined,
    skip_reason_notes: undefined,
    findings: findings || item.findings || '',
    autoLinkedFromForm: true,
  })

  const resetAutoItem = (item) => {
    if (!item.autoLinkedFromForm) return item
    return {
      ...item,
      status: 'not_started',
      skip_reason_code: undefined,
      skip_reason_notes: undefined,
      findings: '',
      autoLinkedFromForm: false,
    }
  }

  return buckets.map((bucket) => {
    if (bucket.id === 'sof') {
      const reason = String(answers.sofNoReason || '').trim()
      const findings = sofCompleted
        ? [
            `SoF enquiry (${answers.sofMethod}) on ${answers.sofDate} by ${answers.sofBy}.`,
            `Source: ${answers.sofCat}${answers.sofOtherDetail ? ` — ${answers.sofOtherDetail}` : ''}.`,
            answers.sowCat ? `SoW: ${answers.sowCat}.` : '',
            `Response: ${answers.sofResp}; CO assessment: ${answers.sofAssess}.`,
            answers.sofEvidence ? `Evidence: ${answers.sofEvidence}` : '',
          ]
            .filter(Boolean)
            .join(' ')
        : ''
      return {
        ...bucket,
        formLinkNote: sofWaived
          ? `Waived by section 4 — SoF/SoW enquiry not required. Reason: ${reason}`
          : sofCompleted
            ? 'Completed via section 4 — SoF/SoW enquiry details above. Checklist mirrored automatically.'
            : sofReq === 'yes'
              ? 'Complete section 4 (SoF/SoW enquiry) — this checklist will update when the enquiry is recorded.'
              : 'Answer section 4 first — if enquiry is not required, document the reason and these items are waived.',
        items: bucket.items.map((item) => {
          if (sofWaived) return waiveItem(item, `SoF/SoW enquiry not required: ${reason}`, 'exemption_applies')
          if (sofCompleted) return completeItem(item, findings)
          return resetAutoItem(item)
        }),
      }
    }

    if (bucket.id === 'verify') {
      return {
        ...bucket,
        formLinkNote: kycCurrent
          ? 'KYC marked current in section 3 — re-verification checklist items waived.'
          : answers.kycCurrent === 'no'
            ? 'KYC not current — complete re-verification in section 3; checklist mirrors that work.'
            : null,
        items: bucket.items.map((item) => {
          if (kycCurrent) {
            return waiveItem(item, 'KYC confirmed current in structured form (section 3).', 'information_already_verified')
          }
          if (answers.kycCurrent === 'no' && answers.verResult && answers.verDate) {
            return completeItem(
              item,
              `Re-verified: ${answers.docType || 'document'} ${answers.docNum || ''} via ${answers.verMethod || '—'} on ${answers.verDate} — ${answers.verResult}.`
            )
          }
          return resetAutoItem(item)
        }),
      }
    }

    if (bucket.id === 'approval' && hasDecision) {
      const approvalNote = meta.requiresApproval === false
        ? 'Closed via structured ECDD — no governance approval trigger matched.'
        : 'Submitted for senior management approval via ECDD decision panel.'
      return {
        ...bucket,
        formLinkNote: 'Management recommendation and approval path recorded in the decision panel.',
        items: bucket.items.map((item) => {
          if (item.id === 'app_1') {
            return completeItem(item, `CO decision (${decision}): ${decisionRationale}`)
          }
          if (item.id === 'app_2') {
            return completeItem(item, approvalNote)
          }
          return item
        }),
      }
    }

    return bucket
  })
}

function BucketSection({
  bucket,
  sectionNumber,
  onStatusChange,
  onFindingsChange,
  onAttachDocuments,
  memberId,
  memberName,
  onShowActivityEvidence,
  caseId,
  readOnly = false,
  defaultOpen = false,
}) {
  const done = bucket.items.filter((i) => i.status === 'done').length
  const naCount = bucket.items.filter((i) => i.status === 'na').length
  const resolved = bucket.items.filter((i) => i.status === 'done' || i.status === 'na').length
  const total = bucket.items.length
  const mandatoryRemaining = bucket.items.filter((i) => i.mandatory && !isMandatoryResolved(i.status)).length
  const isComplete = total > 0 && resolved === total
  const allNa = total > 0 && naCount === total
  const hasMandatory = bucket.items.some((i) => i.mandatory)
  const status = isComplete ? 'done' : hasMandatory ? 'required' : 'optional'
  const statusLabel = isComplete
    ? allNa
      ? 'N/A'
      : naCount > 0
        ? 'Complete'
        : 'Done'
    : mandatoryRemaining > 0
      ? `${mandatoryRemaining} required`
      : hasMandatory
        ? 'Required'
        : total === 0
          ? 'Optional'
          : `${resolved}/${total}`

  return (
    <FormSection
      number={sectionNumber}
      title={bucket.title}
      status={status}
      statusLabel={statusLabel}
      description={bucket.description}
      defaultOpen={defaultOpen || bucket.id === 'custom'}
    >
      {bucket.formLinkNote ? (
        <div className="mb-3 rounded-lg border border-psg-teal/25 bg-[#0A516908] px-3.5 py-2.5 text-[13px] text-psg-teal">
          {bucket.formLinkNote}
        </div>
      ) : null}
      {bucket.items.length === 0 ? (
        <p className="text-sm text-slate-500 m-0">No items yet. Use Add custom item to add measures for this review.</p>
      ) : (
        bucket.items.map((item) => (
          <ChecklistItem
            key={item.id}
            item={item}
            onStatusChange={onStatusChange}
            onFindingsChange={onFindingsChange}
            onAttachDocuments={onAttachDocuments}
            memberId={memberId}
            memberName={memberName}
            onShowActivityEvidence={onShowActivityEvidence}
            caseId={caseId}
            readOnly={readOnly || !!item.autoLinkedFromForm}
          />
        ))
      )}
    </FormSection>
  )
}

export default function ECDDDetail() {
  const { id } = useParams()
  const { can } = useAuth()
  const { toolEnabled } = usePatronScreeningToolEnabled()
  const [searchParams] = useSearchParams()
  const memberNameFromUrl = searchParams.get('memberName') || ''
  const memberIdFromUrl = searchParams.get('memberId') || ''
  const [buckets, setBuckets] = useState(INITIAL_BUCKETS)
  const [formAnswers, setFormAnswers] = useState(() => ({ ...DEFAULT_ECDD_FORM_ANSWERS }))
  const [selectedDecision, setSelectedDecision] = useState(null)
  const [decisionRationale, setDecisionRationale] = useState('')
  const [submittingDecision, setSubmittingDecision] = useState(false)
  const [submitDecisionError, setSubmitDecisionError] = useState('')
  /** Read-only selections preview popup (not live form fields). */
  const [previewOpen, setPreviewOpen] = useState(false)
  const [dynamicFormResponses, setDynamicFormResponses] = useState([])
  /** Last AML/PEP check linked from Screening refresh (replaces manual SCR reference). */
  const [linkedAmlScreening, setLinkedAmlScreening] = useState(null)
  const [noteDraft, setNoteDraft] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)
  const [noteError, setNoteError] = useState('')
  const [baseRecord, setBaseRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [evidenceBrowserOpen, setEvidenceBrowserOpen] = useState(false)
  const [evidenceDefaultLane, setEvidenceDefaultLane] = useState('payouts')
  const [patronScreeningToolOpen, setPatronScreeningToolOpen] = useState(false)
  const [searchAuditRefreshKey, setSearchAuditRefreshKey] = useState(0)
  const [approvingEcdd, setApprovingEcdd] = useState(false)
  const [returningEcdd, setReturningEcdd] = useState(false)
  const [approvalError, setApprovalError] = useState('')
  const [returnReason, setReturnReason] = useState('')
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [closingEcdd, setClosingEcdd] = useState(false)
  const [canApproveEcdd, setCanApproveEcdd] = useState(false)
  const [checkingApprovalPermission, setCheckingApprovalPermission] = useState(true)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  const [activeSectionId, setActiveSectionId] = useState('context')
  const [matchedTriggers, setMatchedTriggers] = useState([])
  const [requiresApproval, setRequiresApproval] = useState(true)
  const [checkingTriggers, setCheckingTriggers] = useState(false)
  /** Latest checklist draft — avoids losing in-progress findings when submit is clicked before textarea blur. */
  const bucketsRef = useRef(INITIAL_BUCKETS)
  const formAnswersRef = useRef(formAnswers)
  const persistDebounceRef = useRef(null)

  useEffect(() => {
    bucketsRef.current = buckets
  }, [buckets])

  useEffect(() => {
    formAnswersRef.current = formAnswers
  }, [formAnswers])

  useEffect(() => {
    return () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setFetchError(null)
      try {
        const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}`))
        const j = await r.json()
        if (cancelled) return
        if (!j.success) throw new Error(j.error || 'Failed to load ECDD')
        setBaseRecord(j.data)
        if (!j.data?.linkedCase) {
          const hydrated = hydrateEcddDraftFromRecord(j.data)
          if (hydrated?.buckets) setBuckets(hydrated.buckets)
          if (hydrated?.decision) setSelectedDecision(hydrated.decision)
          if (hydrated?.rationale) setDecisionRationale(hydrated.rationale)
          setFormAnswers(seedFormAnswersFromRecord(j.data, hydrated?.formAnswers))
        } else {
          setFormAnswers(seedFormAnswersFromRecord(j.data, null))
        }
      } catch (e) {
        if (!cancelled) {
          setFetchError(e.message || 'Failed to load ECDD')
          setBaseRecord(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id])

  useEffect(() => {
    if (!baseRecord?.id) {
      setDynamicFormResponses([])
      return undefined
    }
    const identity = resolvePatronIdentity(baseRecord)
    const patronId =
      identity.master_profile_id ||
      identity.member_id ||
      identity.visitor_id ||
      baseRecord.member_id ||
      ''
    let cancelled = false
    fetchSubjectFormResponses({
      subjectType: 'ecdd',
      subjectId: String(baseRecord.id),
      patronId,
    })
      .then((data) => {
        if (!cancelled) setDynamicFormResponses(data || [])
      })
      .catch(() => {
        if (!cancelled) setDynamicFormResponses([])
      })
    return () => { cancelled = true }
  }, [baseRecord?.id, baseRecord?.member_id])

  const refreshDynamicFormResponses = useCallback(() => {
    if (!baseRecord?.id) return
    const identity = resolvePatronIdentity(baseRecord)
    const patronId =
      identity.master_profile_id ||
      identity.member_id ||
      identity.visitor_id ||
      baseRecord.member_id ||
      ''
    fetchSubjectFormResponses({
      subjectType: 'ecdd',
      subjectId: String(baseRecord.id),
      patronId,
    })
      .then((data) => setDynamicFormResponses(data || []))
      .catch(() => setDynamicFormResponses([]))
  }, [baseRecord])

  const navigate = useNavigate()
  const linkedCaseId = baseRecord?.linkedCase || null
  const isPendingApproval = baseRecord?.status === 'pending_approval'
  const isApprovedMonitoring = baseRecord?.status === 'approved_monitoring'
  const isApprovedSmr = baseRecord?.status === 'approved_smr'
  const isClosed = baseRecord?.status === 'closed'
  const isApproved = isApprovedMonitoring || isApprovedSmr

  // Check if current user can approve ECDDs
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setCheckingApprovalPermission(true)
      try {
        const r = await authFetch(getLocalApiUrl('/api/ecdd-approval-triggers/can-approve'))
        const j = await r.json()
        if (cancelled) return
        if (j.success && j.data) {
          setCanApproveEcdd(j.data.can_approve === true)
        }
      } catch (e) {
        // On error, default to not allowing approval
        if (!cancelled) setCanApproveEcdd(false)
      } finally {
        if (!cancelled) setCheckingApprovalPermission(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const snapshotKey = baseRecord
    ? `${JSON.stringify(baseRecord.bucketsSnapshot ?? null)}|${baseRecord.resolution || ''}`
    : ''

  useEffect(() => {
    if (!baseRecord) return
    let cancelled = false
    ;(async () => {
      try {
        if (linkedCaseId) {
          const r = await authFetch(
            getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/forms/ecdd`)
          )
          const j = await r.json()
          if (cancelled || !j.success || !j.data) return
          const data = typeof j.data === 'string' ? JSON.parse(j.data) : j.data
          if (data.buckets?.length) setBuckets(data.buckets)
          if (data.decision) setSelectedDecision(data.decision)
          if (data.decisionRationale) setDecisionRationale(data.decisionRationale)
          setFormAnswers(seedFormAnswersFromRecord(baseRecord, data.formAnswers))
          return
        }
        const hydrated = hydrateEcddDraftFromRecord(baseRecord)
        if (cancelled || !hydrated) {
          setFormAnswers(seedFormAnswersFromRecord(baseRecord, null))
          return
        }
        if (hydrated.buckets) setBuckets(hydrated.buckets)
        if (hydrated.decision) setSelectedDecision(hydrated.decision)
        if (hydrated.rationale) setDecisionRationale(hydrated.rationale)
        setFormAnswers(seedFormAnswersFromRecord(baseRecord, hydrated.formAnswers))
      } catch {
        /* keep INITIAL_BUCKETS */
      }
    })()
    return () => { cancelled = true }
  }, [baseRecord?.id, snapshotKey, linkedCaseId])

  // Mirror structured SoF/KYC answers onto supporting checklist buckets
  useEffect(() => {
    const status = baseRecord?.status
    const locked = status === 'pending_approval' || status === 'closed' || status === 'approved_monitoring' || status === 'approved_smr'
    if (loading || locked) return
    setBuckets((prev) => {
      const synced = syncBucketsFromFormAnswers(prev, formAnswers)
      bucketsRef.current = synced
      return synced
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when gate-driving form fields change
  }, [
    loading,
    baseRecord?.status,
    formAnswers.sofRequired,
    formAnswers.sofNoReason,
    formAnswers.sofMethod,
    formAnswers.sofDate,
    formAnswers.sofBy,
    formAnswers.sofCat,
    formAnswers.sofResp,
    formAnswers.sofAssess,
    formAnswers.kycCurrent,
    formAnswers.verResult,
    formAnswers.verDate,
    formAnswers.scrResult,
    formAnswers.scrDisp,
    formAnswers.pepClass,
  ])

  // Match Governance Safe → ECDD Approval Policies when decision / form PEP fields change
  useEffect(() => {
    if (!selectedDecision || loading) return
    let cancelled = false
    ;(async () => {
      setCheckingTriggers(true)
      try {
        const body = formAnswersToTriggerCheck(formAnswersRef.current, selectedDecision, baseRecord?.risk)
        const res = await authFetch(getLocalApiUrl('/api/ecdd-approval-triggers/check'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const json = await res.json().catch(() => ({}))
        if (cancelled) return
        if (json.success && json.data) {
          setMatchedTriggers(json.data.matched_triggers || [])
          setRequiresApproval(json.data.requires_approval !== false)
        } else {
          setRequiresApproval(true)
        }
      } catch {
        if (!cancelled) setRequiresApproval(true)
      } finally {
        if (!cancelled) setCheckingTriggers(false)
      }
    })()
    return () => { cancelled = true }
  }, [selectedDecision, formAnswers.pepClass, formAnswers.scrResult, formAnswers.scrDisp, formAnswers.triggerType, baseRecord?.risk, loading])

  useEffect(() => {
    if (loading || !baseRecord) return
    const formGateEval = evaluateEcddFormGates(formAnswers)
    const notesCount = normalizeEcddNotes(baseRecord.notesLog).length
    const sectionIds = buildEcddNavSections({
      formAnswers,
      formGateEval,
      notesCount,
    }).map((s) => s.id)
    const elements = sectionIds
      .map((id) => document.getElementById(`ecdd-section-${id}`))
      .filter(Boolean)
    if (!elements.length) return

    const scrollRoot = document.querySelector('#psg-main-content main')
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          const id = visible[0].target.id.replace('ecdd-section-', '')
          setActiveSectionId(id)
        }
      },
      {
        root: scrollRoot || null,
        rootMargin: '-15% 0px -55% 0px',
        threshold: 0,
      },
    )
    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [loading, baseRecord, formAnswers])

  if (loading) {
    return (
      <div className="space-y-6">
        <Link to="/compliance-safe/ecdd" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD List
        </Link>
        <div className="py-12 text-center text-gray-500">Loading…</div>
      </div>
    )
  }

  if (fetchError || !baseRecord) {
    return (
      <div className="space-y-6">
        <Link to="/compliance-safe/ecdd" className="inline-flex items-center gap-2 text-sm font-medium text-psg-teal hover:underline">
          <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD List
        </Link>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <ExclamationTriangleIcon className="mx-auto mb-2 w-8 h-8 text-red-500" />
            <h2 className="text-lg font-semibold text-red-700">ECDD Record Not Found</h2>
            <p className="text-sm text-red-600 mt-1">{fetchError || `No record found with ID: ${id}`}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // When linked from Case Dashboard, use the case's member name for consistency
  const patronIdentity = resolvePatronIdentity(baseRecord)
  const displayName = (memberNameFromUrl && memberIdFromUrl && String(patronIdentity.member_id || baseRecord.member_id || '') === String(memberIdFromUrl))
    ? memberNameFromUrl
    : (baseRecord.name || baseRecord.member_name || '')
  const profileLinkId =
    patronIdentity.master_profile_id ||
    patronIdentity.member_id ||
    patronIdentity.visitor_id ||
    baseRecord.member_id ||
    ''
  const gamingMemberId = patronIdentity.member_id || baseRecord.member_id || ''
  const membershipNumber = baseRecord.membership_number || patronIdentity.membership_number || ''
  const record = {
    ...baseRecord,
    name: displayName,
    pid: patronIdentity.patron_id || baseRecord.pid || baseRecord.member_id,
    patron_id: patronIdentity.patron_id,
    profileLinkId,
    gamingMemberId,
    membershipNumber,
    kycStatus: 'Verified',
    pepStatus: baseRecord.triggerType === 'pep' ? 'Foreign PEP — Former Deputy Minister of Finance, Greece (2018–2022)' : null,
    triggerDetail: {
      type: baseRecord.triggerType,
      label: baseRecord.trigger,
      description: baseRecord.triggerType === 'pep' ? 'PEP screening during KYC pipeline returned positive match.' : `Investigation triggered by: ${baseRecord.trigger}`,
      source: baseRecord.linkedAlert ? `Alert — ${baseRecord.linkedAlert}` : 'Manual observation',
      linkedAlert: baseRecord.linkedAlert,
      date: baseRecord.triggerDate,
    },
    participants: [`${baseRecord.assignedTo} (CO)`, baseRecord.manager ? `${baseRecord.manager} (Manager)` : null, 'Floor Staff'].filter(Boolean),
  }

  const openActivityEvidence = (lane = 'payouts') => {
    setEvidenceDefaultLane(lane)
    setEvidenceBrowserOpen(true)
  }

  const appendActivitySummary = (result) => {
    const added = Number(result?.added || 0)
    if (!added) return
    const note = `Linked ${added} activity evidence item${added === 1 ? '' : 's'}.`
    const prev = String(formAnswersRef.current.actSummary || '').trim()
    patchFormAnswers({ actSummary: prev ? `${prev}\n${note}` : note })
  }

  const formReadOnly = isPendingApproval || isClosed || isApproved
  const formGateEval = evaluateEcddFormGates(formAnswers)
  const formGatesReady = formGateEval.allOk
  const formGatesTotal = formGateEval.gates.length
  const formGatesDone = formGateEval.gates.filter((g) => g.ok).length
  const formPct = formGatesTotal > 0 ? Math.round((formGatesDone / formGatesTotal) * 100) : 0

  const handlePrintPreview = () => {
    printEcddPreview({
      record,
      formAnswers,
      decision: selectedDecision,
      decisionRationale,
      gates: formGateEval,
      dynamicFormResponses,
    })
  }

  const patchFormAnswers = (patch) => {
    if (formReadOnly) return
    setFormAnswers((prev) => {
      const next = { ...prev, ...patch }
      formAnswersRef.current = next
      const synced = syncBucketsFromFormAnswers(bucketsRef.current, next)
      bucketsRef.current = synced
      setBuckets(synced)
      queuePersistDraft(synced, selectedDecision, decisionRationale, next)
      return next
    })
  }

  const queuePersistDraft = (nextBuckets, decision, rationale, nextFormAnswers) => {
    if (baseRecord?.status !== 'open') return
    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
    persistDebounceRef.current = setTimeout(() => {
      persistEcddDraft(nextBuckets, decision, rationale, nextFormAnswers).catch(() => {})
    }, 500)
  }

  const handleStatusChange = (itemId, newStatus) => {
    setBuckets((prev) => {
      const updated = prev.map((b) => ({
        ...b,
        items: b.items.map((i) => (i.id === itemId ? { ...i, status: newStatus } : i)),
      }))
      bucketsRef.current = updated
      if (baseRecord?.status === 'open') {
        if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
        persistEcddDraft(updated, selectedDecision, decisionRationale).catch(() => {})
      }
      return updated
    })
  }

  const handleFindingsChange = (itemId, findings) => {
    setBuckets((prev) => {
      const updated = prev.map((b) => ({
        ...b,
        items: b.items.map((i) => (i.id === itemId ? { ...i, findings } : i)),
      }))
      bucketsRef.current = updated
      queuePersistDraft(updated, selectedDecision, decisionRationale)
      return updated
    })
  }

  const handleAttachDocuments = (itemId, fileNames) => {
    if (!fileNames?.length) return
    setBuckets((prev) => {
      const updated = prev.map((b) => ({
        ...b,
        items: b.items.map((i) => {
          if (i.id !== itemId) return i
          if (i.isKyc) {
            return { ...i, kycDocs: [...(i.kycDocs || []), ...fileNames] }
          }
          return { ...i, docs: [...(i.docs || []), ...fileNames] }
        }),
      }))
      bucketsRef.current = updated
      if (baseRecord?.status === 'open') {
        if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
        persistEcddDraft(updated, selectedDecision, decisionRationale).catch(() => {})
      }
      return updated
    })
  }

  const handleAddEcddNote = async () => {
    const text = noteDraft.trim()
    if (!text || notesSaving) return
    setNotesSaving(true)
    setNoteError('')
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/notes`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to save note')
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data } : prev))
      setNoteDraft('')
    } catch (e) {
      setNoteError(e.message || 'Failed to save note')
    } finally {
      setNotesSaving(false)
    }
  }

  const persistEcddDraft = async (nextBuckets, decision, rationale, nextFormAnswers) => {
    const answers = nextFormAnswers ?? formAnswersRef.current
    const payload = {
      buckets: nextBuckets ?? buckets,
      decision: decision ?? selectedDecision,
      decisionRationale: (rationale ?? decisionRationale).trim(),
      formAnswers: answers,
    }
    if (linkedCaseId) {
      const r = await authFetch(
        getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/forms/ecdd`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: payload }),
        }
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || j.success === false) throw new Error(j.error || 'Failed to save ECDD draft')
      return
    }
    const currentBuckets = nextBuckets ?? buckets
    const items = currentBuckets.flatMap((b) => b.items)
    const mandatory = items.filter((i) => i.mandatory)
    const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        checklist_done: items.filter((i) => i.status === 'done' || i.status === 'na').length,
        checklist_mandatory_done: mandatory.filter((i) => isMandatoryResolved(i.status)).length,
        buckets: currentBuckets,
        decision: decision ?? selectedDecision,
        decisionRationale: (rationale ?? decisionRationale).trim(),
        formAnswers: answers,
      }),
    })
    const j = await r.json().catch(() => ({}))
    if (!r.ok || j.success === false) {
      throw new Error(j.error || 'Failed to save ECDD draft')
    }
    if (j.data?.bucketsSnapshot) {
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data } : prev))
    }
  }

  const handleSubmitForApproval = async () => {
    if (!selectedDecision || !decisionRationale.trim() || submittingDecision || isPendingApproval || isClosed) return
    if (!formGatesReady) {
      setSubmitDecisionError('Complete the required form sections before submitting.')
      return
    }
    setSubmitDecisionError('')
    setSubmittingDecision(true)
    try {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current)
        persistDebounceRef.current = null
      }
      const draftAnswers = formAnswersRef.current

      // Re-check Approval Policies (Governance Safe) before submit
      let approvalNeeded = true
      try {
        const triggerRes = await authFetch(getLocalApiUrl('/api/ecdd-approval-triggers/check'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formAnswersToTriggerCheck(draftAnswers, selectedDecision, baseRecord?.risk)),
        })
        const triggerJson = await triggerRes.json().catch(() => ({}))
        if (triggerJson.success && triggerJson.data) {
          setMatchedTriggers(triggerJson.data.matched_triggers || [])
          approvalNeeded = triggerJson.data.requires_approval !== false
          setRequiresApproval(approvalNeeded)
        }
      } catch {
        approvalNeeded = true
      }

      const draftBuckets = syncBucketsFromFormAnswers(bucketsRef.current, draftAnswers, {
        decision: selectedDecision,
        decisionRationale: decisionRationale.trim(),
        requiresApproval: approvalNeeded,
      })
      const payload = {
        buckets: draftBuckets,
        decision: selectedDecision,
        decisionRationale: decisionRationale.trim(),
        formAnswers: draftAnswers,
      }
      await persistEcddDraft(draftBuckets, selectedDecision, decisionRationale.trim(), draftAnswers)

      // If linked to a case, ensure ECDD record exists before submitting
      if (linkedCaseId) {
        const createRes = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/create-ecdd`),
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
        )
        const createJson = await createRes.json().catch(() => ({}))
        if (!createRes.ok || !createJson.success) {
          throw new Error(createJson.error || 'Failed to create ECDD record')
        }
      }

      const submitUrl = linkedCaseId
        ? getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/forms/ecdd/submit`)
        : getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/submit-for-approval`)
      const body = linkedCaseId
        ? { data: payload, directClose: !approvalNeeded }
        : { ...payload, directClose: !approvalNeeded }
      const r = await authFetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.errors?.[0]?.message || j.error || 'Submit failed')
      }
      const nextStatus = !approvalNeeded ? 'closed' : 'pending_approval'
      const merged = { ...(j.data || {}), status: j.data?.status || nextStatus }
      setBaseRecord((prev) => (prev ? { ...prev, ...merged } : prev))
      const hydrated = hydrateEcddDraftFromRecord(merged)
      if (hydrated?.buckets) setBuckets(hydrated.buckets)
      if (hydrated?.decision) setSelectedDecision(hydrated.decision)
      if (hydrated?.rationale) setDecisionRationale(hydrated.rationale)
      if (hydrated?.formAnswers) setFormAnswers(mergeFormAnswers(hydrated.formAnswers))
    } catch (e) {
      setSubmitDecisionError(e.message || 'Failed to submit for manager approval')
    } finally {
      setSubmittingDecision(false)
    }
  }

  const handleApproveEcdd = async () => {
    if (approvingEcdd) return
    setApprovalError('')
    setApprovingEcdd(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/approve`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.error || 'Approval failed')
      }
      // Update status based on the decision outcome
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data, status: j.newStatus || j.data?.status } : prev))
      
      // Show success message based on decision
      if (j.message) {
        alert(j.message)
      }
    } catch (e) {
      setApprovalError(e.message || 'Failed to approve ECDD')
    } finally {
      setApprovingEcdd(false)
    }
  }

  const handleReturnEcdd = async () => {
    if (returningEcdd || !returnReason.trim()) return
    setApprovalError('')
    setReturningEcdd(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/return`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnReason: returnReason.trim() }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.error || 'Return failed')
      }
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data, status: 'open' } : prev))
      setShowReturnModal(false)
      setReturnReason('')
    } catch (e) {
      setApprovalError(e.message || 'Failed to return ECDD')
    } finally {
      setReturningEcdd(false)
    }
  }

  const handleCloseEcdd = async (smrReference = '') => {
    if (closingEcdd) return
    setApprovalError('')
    setClosingEcdd(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/close`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ smrReference }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.error || 'Close failed')
      }
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data, status: 'closed' } : prev))
      if (j.message) {
        alert(j.message)
      }
    } catch (e) {
      setApprovalError(e.message || 'Failed to close ECDD')
    } finally {
      setClosingEcdd(false)
    }
  }

  const riskColor = record.risk >= 70 ? '#dc2626' : record.risk >= 40 ? '#ea580c' : '#16a34a'
  const ecddNotes = normalizeEcddNotes(baseRecord.notesLog)
  const ecddNotesNewestFirst = [...ecddNotes].reverse()

  const scrollToFirstIncomplete = () => {
    const first = firstRequiredEcddSection({
      formAnswers,
      formGateEval,
      notesCount: ecddNotes.length,
    })
    if (first?.id) {
      scrollToEcddSection(first.id)
      setActiveSectionId(first.id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Full-width top row: breadcrumb left, Help right — panels sit below this */}
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/compliance-safe/ecdd"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600 transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD List
        </Link>
        <HelpScreenButton
          screenKey={ECDD_DETAIL_HELP_SCREEN_KEY}
          onClick={() => setHelpPanelOpen(true)}
        />
      </div>

      <Card className="overflow-hidden border border-slate-100 border-t-4 border-t-[#FDE300]">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-[#073B4D] m-0">Enhanced Customer Due Diligence Record</h1>
              <p className="text-xs text-slate-400 mt-1 mb-0">Compliance Safe · ECDD · {record.id}</p>
              <h2 className="text-base font-semibold text-psg-teal mt-3 mb-0">{record.name}</h2>
              <div className="mt-1.5">
                <PatronIdBadge
                  patronId={patronIdentity.master_profile_id || patronIdentity.patron_id}
                  memberId={patronIdentity.member_id}
                  visitorId={patronIdentity.visitor_id}
                  profileType={patronIdentity.subject_type === 'member' ? 'MEMBER' : patronIdentity.subject_type === 'visitor' ? 'VISITOR' : null}
                  variant="compact"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
                onClick={() => setPreviewOpen(true)}
                title="Read-only preview of recorded selections"
              >
                <EyeIcon className="w-4 h-4 mr-1" />
                Preview
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
                onClick={handlePrintPreview}
                title="Print read-only preview of recorded selections"
              >
                <PrinterIcon className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Link to={`/profile-safe/search?memberId=${encodeURIComponent(profileLinkId)}`}>
                <Button variant="outline" size="sm"><EyeIcon className="w-4 h-4 mr-1" /> View Profile</Button>
              </Link>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => {
                  if (linkedCaseId) {
                    navigate(
                      `/compliance-safe/investigations/${encodeURIComponent(linkedCaseId)}?tab=smr_form`
                    )
                    return
                  }
                  if (baseRecord?.linkedSmr) {
                    navigate('/compliance-safe/smr-registry')
                    return
                  }
                  window.alert(
                    'No investigation case is linked to this ECDD. Complete manager approval with Escalate → SMR, or start an investigation from the payout / alert workflow first.'
                  )
                }}
              >
                <ExclamationTriangleIcon className="w-4 h-4 mr-1" /> Create SMR Now
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-6 lg:items-stretch">
        <div
          className="mb-3 lg:mb-0 shrink-0 min-h-0 flex flex-col"
          aria-label="Form navigation and attachments"
        >
          <div className="sticky top-0 z-30 shrink-0 w-full">
            <EcddDetailNav
              formAnswers={formAnswers}
              formGateEval={formGateEval}
              formPct={formPct}
              formGatesDone={formGatesDone}
              formGatesTotal={formGatesTotal}
              formGatesReady={formGatesReady}
              notesCount={ecddNotes.length}
              activeSectionId={activeSectionId}
              onNavigate={setActiveSectionId}
            />
          </div>
          <FormsModuleGate>
            <div className="mt-3 lg:mt-4">
              <RecordFormsPanel
                subjectType="ecdd"
                subjectId={String(record.id || id || '')}
                subjectLabel={record.name || 'This ECDD'}
                patronId={profileLinkId || ''}
                readOnly={formReadOnly || !can('compliance-safe/forms', 'assign')}
                title="Forms"
                onAssignmentsChange={refreshDynamicFormResponses}
              />
            </div>
          </FormsModuleGate>
          <div className="mt-3 lg:mt-4">
            <EcddAttachedDocumentsRail
              ecddId={record.id || id}
              formAnswers={formAnswers}
              variant="sidebar"
            />
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <SectionAnchor id="context">
          <FormSection
            number={1}
            title="Case context, risk & linked records"
            status={record.linkedSmr || record.triggerType === 'pep' ? 'required' : 'done'}
            statusLabel={record.status === 'open' ? 'Open' : record.status === 'pending_approval' ? 'Pending approval' : record.status === 'approved_smr' ? 'SMR pending' : record.status === 'approved_monitoring' ? 'Monitoring' : 'Closed'}
            defaultOpen
            description="Read this first — status, risk, trigger, and any linked alert or SMR."
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-3 mb-4">
              {[
                ['ECDD ID', record.id],
                ['Status', record.status === 'open' ? 'Open' : record.status === 'pending_approval' ? 'Pending Approval' : record.status === 'approved_monitoring' ? 'Approved — Monitoring' : record.status === 'approved_smr' ? 'Approved — SMR Pending' : 'Closed'],
                ['Days open', String(record.daysOpen ?? '—')],
                ['Risk score', String(record.risk ?? '—')],
                ['KYC at open', record.kycStatus || '—'],
                ['Assigned CO', record.assignedTo || '—'],
                ['Created', record.triggerDate || '—'],
                ['Venue', record.venue || record.venueName || '—'],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
                  <div
                    className="text-sm font-medium text-slate-700 mt-0.5"
                    style={label === 'Risk score' ? { color: riskColor, fontWeight: 700 } : undefined}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3.5 py-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <LockClosedIcon className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Trigger</span>
                {record.triggerType === 'pep' && (
                  <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">Foreign PEP</Badge>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Type</div>
                  <div className="font-semibold text-psg-teal">{record.triggerDetail.label}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Date</div>
                  <div className="text-slate-700">{record.triggerDetail.date || '—'}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-slate-500">Source</div>
                  <div className="text-slate-700">{record.triggerDetail.source}</div>
                </div>
                {record.triggerDetail.description && (
                  <div className="sm:col-span-2">
                    <div className="text-xs text-slate-500">Description</div>
                    <div className="text-slate-700">{record.triggerDetail.description}</div>
                  </div>
                )}
              </div>
              {record.pepStatus && (
                <div className="mt-3 pt-3 border-t border-slate-200 text-sm font-semibold text-red-600">
                  {record.pepStatus}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div className={`rounded-lg border px-3.5 py-3 ${record.linkedAlert ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200 bg-white'}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Linked alert</div>
                <div className="text-sm font-medium text-slate-800 break-words">{record.linkedAlert || record.triggerDetail.linkedAlert || 'None'}</div>
              </div>
              <div className={`rounded-lg border px-3.5 py-3 ${record.linkedSmr ? 'border-red-200 bg-red-50/70' : 'border-slate-200 bg-white'}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Linked SMR</div>
                <div className={`text-sm font-medium break-words ${record.linkedSmr ? 'text-red-800' : 'text-slate-800'}`}>
                  {record.linkedSmr || 'None'}
                </div>
                {record.linkedSmr && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-red-700 hover:underline mt-1.5 bg-transparent border-none p-0 cursor-pointer"
                    onClick={() => navigate('/compliance-safe/smr-registry')}
                  >
                    Open SMR registry →
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center mb-3">
              <Badge bg="#e8f2f6" color="#0a5169" border="#b8d8e8">
                {record.status === 'open' ? 'Open' : record.status === 'pending_approval' ? 'Pending Approval' : record.status === 'approved_monitoring' ? 'Approved — Monitoring' : record.status === 'approved_smr' ? 'Approved — SMR Pending' : 'Closed'}
              </Badge>
              {record.triggerType === 'pep' && <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">Foreign PEP</Badge>}
              {record.linkedSmr && <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">Has SMR</Badge>}
            </div>

            <div className="pt-3 border-t border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Team</div>
              <div className="flex flex-wrap gap-2 items-center">
                {record.participants.map((p, i) => (
                  <span key={i} className="text-sm px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{p}</span>
                ))}
              </div>
            </div>
          </FormSection>
          </SectionAnchor>

          <SectionAnchor id="trigger">
          <FormSection
            number={2}
            title="Trigger"
            status={formGateEval.gates.find((g) => g.id === 'trigger')?.ok ? 'done' : 'required'}
            statusLabel={formAnswers.triggerType ? 'In progress' : 'Required'}
            defaultOpen
            description={
              isLargePayoutTrigger(formAnswers)
                ? 'Large payout ECDD — select trigger type; patron identity was established at payout.'
                : 'Record why this ECDD was opened — type, description, and linked references.'
            }
          >
            <EcddTriggerFields formAnswers={formAnswers} onChange={patchFormAnswers} readOnly={formReadOnly} />
          </FormSection>
          </SectionAnchor>

          {!isLargePayoutTrigger(formAnswers) && (
          <SectionAnchor id="kyc">
          <FormSection
            number={3}
            title="KYC / identity"
            status={formGateEval.gates.find((g) => g.id === 'kyc')?.ok ? 'done' : 'required'}
            statusLabel={formAnswers.kycCurrent || 'Required'}
            defaultOpen={false}
            description="Confirm KYC currency; re-verify when not current."
          >
            <EcddKycFields formAnswers={formAnswers} onChange={patchFormAnswers} readOnly={formReadOnly} />
          </FormSection>
          </SectionAnchor>
          )}

          <SectionAnchor id="sof">
          <FormSection
            number={4}
            title="Source of funds / wealth"
            status={formGateEval.gates.find((g) => g.id === 'sof')?.ok ? 'done' : 'required'}
            statusLabel={formGateEval.foreignPep ? 'Mandatory (foreign PEP)' : 'Risk-based'}
            defaultOpen={false}
            description="SoF/SoW enquiry — when required, record evidence and attach supporting documents. OSINT / PEP checks sit under Screening refresh."
          >
            <EcddSofFields
              formAnswers={formAnswers}
              onChange={patchFormAnswers}
              readOnly={formReadOnly}
              formsReadOnly={formReadOnly || !can('compliance-safe/forms', 'assign')}
              ecddId={record.id || id}
              patronId={profileLinkId}
              patronLabel={record.name}
              onFormResponsesChange={refreshDynamicFormResponses}
            />
          </FormSection>
          </SectionAnchor>

          <SectionAnchor id="activity">
          <FormSection
            number={5}
            title="Activity analysis"
            status={formGateEval.gates.find((g) => g.id === 'activity')?.ok === false ? 'required' : 'optional'}
            statusLabel={formGateEval.actMandatory ? 'Required for this trigger' : 'As needed'}
            defaultOpen={false}
            description="Review gaming/transaction pattern against profile and SoF."
          >
            <EcddActivityFields
              formAnswers={formAnswers}
              onChange={patchFormAnswers}
              readOnly={formReadOnly}
              ecddId={record.id || id}
              activityHelpers={
                record.membershipNumber || record.gamingMemberId ? (
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button type="button" variant="outline" size="sm" onClick={() => openActivityEvidence('payouts')}>
                      Review & link activity
                    </Button>
                  </div>
                ) : null
              }
            />
          </FormSection>
          </SectionAnchor>

          <SectionAnchor id="engage">
          <FormSection
            number={6}
            title="Patron engagement"
            status={formGateEval.gates.find((g) => g.id === 'engage')?.ok ? 'done' : 'required'}
            statusLabel="Tipping-off sensitive"
            defaultOpen={false}
            description="Record engagement and tipping-off declarations where required."
          >
            <EcddEngageFields formAnswers={formAnswers} onChange={patchFormAnswers} readOnly={formReadOnly} />
          </FormSection>
          </SectionAnchor>

          <SectionAnchor id="screen">
          <FormSection
            number={7}
            title="Screening refresh"
            status={formGateEval.gates.find((g) => g.id === 'screen')?.ok ? 'done' : 'required'}
            statusLabel={
              formGateEval.gates.find((g) => g.id === 'screen')?.ok
                ? hasScreeningOnRecord(formAnswers)
                  ? 'On file'
                  : 'Done'
                : formAnswers.scrResult === 'hit'
                  ? 'Hit — disposition required'
                  : 'Required'
            }
            defaultOpen={false}
            description="Re-screen result and PEP classification feed Approval Policies (Governance Safe)."
          >
            <EcddAmlPepPanel
              variant="inline"
              memberId={record.gamingMemberId || record.member_id || ''}
              memberName={record.name}
              reason="ECDD re-screen"
              showOsintButton={
                isPatronScreeningPrototypeEnabled() &&
                can('compliance-safe/patron-screening', 'view') &&
                toolEnabled
              }
              onOpenOsintScreening={() => setPatronScreeningToolOpen(true)}
              onHistoryLoaded={({ count, latest }) => {
                const patch = formReadOnly
                  ? { amlScreeningCount: count }
                  : patchFormFromAmlHistory(formAnswersRef.current, { count, latest })
                patchFormAnswers(patch)
                if (latest?.id) {
                  setLinkedAmlScreening({
                    checkId: latest.id,
                    hitCount: Number(latest.result_count) || 0,
                    at: latest.created_at,
                  })
                }
              }}
              onScreened={({ checkId, hitCount, at }) => {
                setLinkedAmlScreening({ checkId, hitCount, at })
                if (formReadOnly) return
                const today = (at ? new Date(at) : new Date()).toISOString().slice(0, 10)
                const cur = formAnswersRef.current
                const patch = {
                  scrDate: cur.scrDate || today,
                  scrResult: cur.scrResult || (hitCount > 0 ? 'hit' : 'clear'),
                  amlScreeningCount: Math.max(Number(cur.amlScreeningCount) || 0, 1),
                }
                if (checkId) patch.scrRef = String(checkId)
                patchFormAnswers(patch)
              }}
            />
            <EcddScreeningFields
              formAnswers={formAnswers}
              onChange={patchFormAnswers}
              readOnly={formReadOnly}
              linkedScreeningNote={
                linkedAmlScreening?.checkId
                  ? `Linked AML/PEP check SCR-${linkedAmlScreening.checkId}${
                      linkedAmlScreening.hitCount != null
                        ? ` — ${linkedAmlScreening.hitCount} sample match${linkedAmlScreening.hitCount === 1 ? '' : 'es'} returned`
                        : ''
                    }. Open history above to view detail.`
                  : formAnswers.scrRef
                    ? `Legacy screening reference on file: ${formAnswers.scrRef}`
                    : null
              }
            />
            {isPatronScreeningPrototypeEnabled() && can('compliance-safe/patron-screening', 'view') && toolEnabled && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <SearchAuditPanel
                  patronId={record.gamingMemberId || null}
                  memberName={record.name}
                  refreshKey={searchAuditRefreshKey}
                  canOpenScreening
                  onOpenScreening={() => setPatronScreeningToolOpen(true)}
                />
              </div>
            )}
          </FormSection>
          </SectionAnchor>

          <SectionAnchor id="notes">
          <FormSection
            number={8}
            title="General notes"
            status="optional"
            statusLabel={`${ecddNotes.length} note${ecddNotes.length !== 1 ? 's' : ''}`}
            defaultOpen={false}
            description="Dated entries for this ECDD — each note is saved with a timestamp."
          >
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      handleAddEcddNote()
                    }
                  }}
                  placeholder="Write a note… (Ctrl+Enter or ⌘+Enter to add)"
                  rows={4}
                  disabled={notesSaving}
                  className="flex-1 min-h-[100px] p-3 rounded-lg border border-gray-200 text-sm text-gray-800 bg-white resize-y outline-none focus:border-gray-300 leading-relaxed disabled:opacity-60"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="sm:self-end shrink-0 border-psg-teal text-psg-teal hover:bg-psg-teal/5"
                  disabled={notesSaving || !noteDraft.trim()}
                  onClick={() => handleAddEcddNote()}
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1" />
                  {notesSaving ? 'Saving…' : 'Add note'}
                </Button>
              </div>
              {noteError && <p className="text-sm text-red-600 mb-2">{noteError}</p>}
              {ecddNotesNewestFirst.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">No notes yet.</p>
              ) : (
                <ul className="border-t border-gray-100 pt-3 space-y-3">
                  {ecddNotesNewestFirst.map((n) => (
                    <li key={n.id} className="border-l-2 border-psg-teal/30 pl-3">
                      <div className="text-xs text-gray-500 font-mono mb-0.5">
                        {formatEcddNoteTs(n.ts)} · <span className="font-sans font-medium text-gray-600">{n.who}</span>
                      </div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{n.text}</div>
                    </li>
                  ))}
                </ul>
              )}
          </FormSection>
          </SectionAnchor>

          <SectionAnchor id="decision">
            <EcddDecisionPanel
              isClosed={isClosed}
              isPendingApproval={isPendingApproval}
              isApproved={isApproved}
              isApprovedSmr={isApprovedSmr}
              isApprovedMonitoring={isApprovedMonitoring}
              formGatesReady={formGatesReady}
              formGatesDone={formGatesDone}
              formGatesTotal={formGatesTotal}
              selectedDecision={selectedDecision}
              setSelectedDecision={setSelectedDecision}
              decisionRationale={decisionRationale}
              setDecisionRationale={setDecisionRationale}
              submittingDecision={submittingDecision}
              submitDecisionError={submitDecisionError}
              checkingTriggers={checkingTriggers}
              matchedTriggers={matchedTriggers}
              requiresApproval={requiresApproval}
              checkingApprovalPermission={checkingApprovalPermission}
              canApproveEcdd={canApproveEcdd}
              approvingEcdd={approvingEcdd}
              returningEcdd={returningEcdd}
              approvalError={approvalError}
              showReturnModal={showReturnModal}
              setShowReturnModal={setShowReturnModal}
              returnReason={returnReason}
              setReturnReason={setReturnReason}
              closingEcdd={closingEcdd}
              onSubmitForApproval={handleSubmitForApproval}
              onApproveEcdd={handleApproveEcdd}
              onReturnEcdd={handleReturnEcdd}
              onCloseEcdd={handleCloseEcdd}
              onOpenPreview={() => setPreviewOpen(true)}
              onPrintPreview={handlePrintPreview}
              onScrollToIncomplete={scrollToFirstIncomplete}
            />
          </SectionAnchor>
        </div>
      </div>

      <EvidenceLinkBrowser
        display="modal"
        isOpen={evidenceBrowserOpen}
        onClose={() => setEvidenceBrowserOpen(false)}
        onLinked={appendActivitySummary}
        context={{ type: 'ecdd', id: record.id || id }}
        defaultMemberNumber={record.membershipNumber || ''}
        defaultPlayerId={record.gamingMemberId || ''}
        defaultMemberName={record.name}
        defaultLane={evidenceDefaultLane}
        showCsvTab={false}
      />
      <PatronScreeningToolModal
        open={patronScreeningToolOpen}
        onClose={() => {
          setPatronScreeningToolOpen(false)
          setSearchAuditRefreshKey((k) => k + 1)
          if (!formReadOnly) {
            const today = new Date().toISOString().slice(0, 10)
            if (!formAnswersRef.current.scrDate) {
              patchFormAnswers({ scrDate: today })
            }
          }
        }}
        memberName={record.name}
        memberId={record.gamingMemberId}
      />
      <EcddDocumentPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        record={record}
        formAnswers={formAnswers}
        decision={selectedDecision}
        decisionRationale={decisionRationale}
        gates={formGateEval}
        dynamicFormResponses={dynamicFormResponses}
      />
      <ScreenHelpPanel
        screenKey={ECDD_DETAIL_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="ecdd-detail-help"
      />
    </div>
  )
}
